/**
 * Admission controller that paces sandboxed-iframe creation instead of letting
 * a canvas full of HTML blocks mount every frame in one burst.
 *
 * Why this exists: every block node used to mount its srcdoc iframe
 * simultaneously (React Flow renders all nodes regardless of viewport). On
 * block-heavy canvases that first-paint burst — dozens of documents each
 * parsing fonts, injected CSS, the block runtime, and infinite CSS animations
 * inside ~1s — reliably killed the WebKit renderer process on iPhones
 * (mach-port EXC_GUARD, "Webpage Crashed"). Pacing the mounts keeps the same
 * end state while staying under what WebKit tolerates.
 *
 * Behavior:
 * - Cards register their host element once their document is READY to mount
 *   (srcDoc prepared — see useIframeMountAdmission), so an admission is
 *   always immediately followed by a real iframe mount and the load event is
 *   what returns the slot. Registering un-ready cards would let slots leak
 *   through the fallback timeout while fetches are still in flight, and a
 *   slow network would then re-cluster the very burst this exists to prevent.
 * - One shared IntersectionObserver ranks waiting cards by viewport proximity
 *   (IO measures the on-screen rect, so it works under React Flow's CSS
 *   transforms). Its root is expanded by `observerRootMargin`, which is how
 *   far ahead of a pan a block starts.
 * - Admissions start only after a settle delay from the first registration,
 *   so the initial rasterization happens at the post-fitView scale rather
 *   than mid-animation.
 * - At most `maxConcurrentInits` iframes are initializing at once; a slot
 *   frees on the iframe's load event, with a generous fallback timeout as a
 *   stuck-detector (a document whose load never fires must not wedge the
 *   queue). A minimum spacing between admissions spreads compositor work;
 *   fully offscreen cards trickle at a slower cadence.
 * - Admission is ONE-WAY: nothing is ever un-admitted on scroll-away, so a
 *   canvas ends up with every block live. (The rejected alternatives —
 *   React Flow's `onlyRenderVisibleElements`, and a live-frame budget that
 *   retires far-away documents — both churn iframes on every pan, which
 *   recreates exactly the load spikes that crash WebKit; measured on a
 *   303-block canvas a budget small enough to matter cost more in pan
 *   jank than the frames it saved.)
 */

/**
 * How fast documents are allowed to start, per device. WebKit on an iPhone is
 * the floor this was written for, and an iPad runs the same engine: a narrow
 * pipe (2 at once) and a real gap
 * between admissions (150 ms, 250 ms for the offscreen trickle), with a
 * 600 ms settle so the burst does not land on the fitView animation. That
 * pacing is what stops the renderer crash, so it stays as shipped.
 *
 * Desktop has no such ceiling and the gap bought nothing there: what bounds
 * the work is `maxConcurrentInits`, not a timer, so spacing is 0 and the pipe
 * is 32 wide. Its settle is 150 ms — long enough for node measurement, short
 * enough that the first block is not waiting on a timer — and its observer
 * root is a full viewport wide so a pan starts documents before they scroll
 * in.
 */
export interface IframeMountPacing {
  /** Iframes allowed to be initializing (admitted, not yet loaded) at once. */
  maxConcurrentInits: number;
  /** Minimum gap between admissions for near-viewport cards. */
  admitSpacingMs: number;
  /** Slower cadence once only fully-offscreen cards remain. */
  offscreenTrickleSpacingMs: number;
  /** Wait out node measurement + the fitView animation before admitting. */
  startDelayMs: number;
  /**
   * How far beyond the visible rect a card counts as near the viewport, as an
   * IntersectionObserver `rootMargin`. This is how early a block starts: at
   * "100%" a pan admits it a full viewport before it scrolls in.
   *
   * It is also the ranking's resolution, and the two pull against each other.
   * `intersectionRatio` is measured against the margin-EXPANDED root, so a
   * margin large enough to report ratio 1 for everything near the viewport
   * collapses viewport-first admission into registration order. Desktop can
   * afford a wide one because it admits everything in the end anyway; iOS
   * cannot, and keeps the ranking sharp instead.
   */
  observerRootMargin: string;
  /**
   * Documents this surface may have live at once. `Infinity` on every
   * interactive surface: a reader who pans to a block expects to find a live
   * one, and measurement on a 303-block canvas said a cap small enough to
   * save memory costs more in pan jank than the frames it saves. The capture
   * is the one surface with a real ceiling (see CAPTURE_PACING).
   */
  maxAdmitted: number;
}

const IOS_PACING: IframeMountPacing = {
  maxConcurrentInits: 2,
  admitSpacingMs: 150,
  offscreenTrickleSpacingMs: 250,
  startDelayMs: 600,
  observerRootMargin: "50%",
  maxAdmitted: Infinity,
};

const DESKTOP_PACING: IframeMountPacing = {
  maxConcurrentInits: 32,
  admitSpacingMs: 0,
  offscreenTrickleSpacingMs: 0,
  startDelayMs: 150,
  observerRootMargin: "100%",
  maxAdmitted: Infinity,
};

/** Read once per module load: the device does not change mid-session. */
export const devicePacing: IframeMountPacing =
  typeof navigator !== "undefined" &&
  (/iPhone|iPad|iPod/.test(navigator.userAgent) ||
    // iPadOS Safari reports as Macintosh; a Mac UA with touch is an iPad.
    (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1))
    ? IOS_PACING
    : DESKTOP_PACING;

/**
 * The screenshot capture (thumbnail lambda, publish OG image, CLI capture):
 * one headless Chromium, no reader and no pan, so it needs no settle for an
 * animation it never runs and no lookahead margin.
 *
 * `maxAdmitted` is the point of it. The capture used to mount every block
 * eagerly, and on a block-heavy canvas the lambda's Chromium ran out of /tmp
 * at around 150 documents in flight and lost the whole capture. Past the cap
 * a block is captured as its poster — a picture of that same document,
 * rendered server-side — and the slots go first to the blocks that have no
 * poster to fall back on (chromeless ones, which hide the opaque JPEG, and
 * ones whose poster has not been rendered yet). So the cap costs the shot
 * nothing unless more than `maxAdmitted` blocks have no poster. A
 * whole-canvas capture with more live-capable nodes than this shows posters
 * only (the `"poster"` policy).
 */
export const CAPTURE_LIVE_LIMIT = 32;

const CAPTURE_PACING: IframeMountPacing = {
  maxConcurrentInits: 8,
  admitSpacingMs: 0,
  offscreenTrickleSpacingMs: 0,
  startDelayMs: 0,
  observerRootMargin: "0%",
  maxAdmitted: CAPTURE_LIVE_LIMIT,
};

/**
 * Stuck-detector ONLY: a slot frees early if an admitted iframe never reports
 * load (dead frame, broken subresource). Sized well above a slow device's
 * worst-case document init so the load event — not this timer — is what
 * returns slots in normal operation; a short value here would silently turn
 * the concurrency cap into a mere admission-rate limit.
 */
const INIT_FALLBACK_TIMEOUT_MS = 5000;

export interface IframeMountHandle {
  /** The admitted iframe finished loading — frees its concurrency slot. */
  loaded(): void;
  /** Unregister (host unmounted). Safe to call at any time, and idempotent. */
  release(): void;
}

interface Entry {
  element: Element;
  onAdmit: () => void;
  order: number;
  /** The card has a picture to show while it waits. Read once at
   *  registration: a poster that arrives later does not re-rank the queue. */
  hasPoster: boolean;
  /** Last intersection ratio; 0 until the observer reports (or without IO). */
  ratio: number;
  admitted: boolean;
  /** Load reported, fallback timeout fired, or released — slot returned. */
  settled: boolean;
  fallbackTimer: ReturnType<typeof setTimeout> | null;
}

type ObserverFactory = (
  onEntries: (entries: IntersectionObserverEntry[]) => void,
) => IntersectionObserver | null;

const defaultObserverFactory =
  (rootMargin: string): ObserverFactory =>
  (onEntries) => {
    // Environments without IntersectionObserver (jsdom, ancient browsers)
    // degrade to registration-order trickle — pacing still applies.
    if (typeof IntersectionObserver === "undefined") return null;
    return new IntersectionObserver(onEntries, {
      rootMargin,
      threshold: [0, 0.01, 0.25, 0.5, 0.75, 1],
    });
  };

export class IframeMountScheduler {
  private entries = new Map<Element, Entry>();
  /** `undefined` = not built yet (lazy); `null` = IO unavailable. */
  private observer: IntersectionObserver | null | undefined;
  private nextOrder = 0;
  private started = false;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private pumpTimer: ReturnType<typeof setTimeout> | null = null;
  private pumpDueAt = Infinity;
  private initializing = 0;
  private admittedCount = 0;
  private lastAdmitAt: number | null = null;
  private readonly createObserver: ObserverFactory;
  private readonly pacing: IframeMountPacing;

  constructor(
    createObserver?: ObserverFactory,
    pacing: IframeMountPacing = devicePacing,
  ) {
    this.pacing = pacing;
    this.createObserver =
      createObserver ?? defaultObserverFactory(pacing.observerRootMargin);
  }

  register(
    element: Element,
    onAdmit: () => void,
    { hasPoster = false }: { hasPoster?: boolean } = {},
  ): IframeMountHandle {
    if (this.observer === undefined) {
      this.observer = this.createObserver(this.handleObserverEntries);
    }

    // A lingering registration for the same element (remount race, Fast
    // Refresh) would let its stale handle evict THIS entry later — release()
    // must therefore also check identity, and the old entry goes now so the
    // map and observer only ever know one entry per element.
    const prior = this.entries.get(element);
    if (prior) this.release(prior);

    const entry: Entry = {
      element,
      onAdmit,
      order: this.nextOrder++,
      hasPoster,
      ratio: 0,
      admitted: false,
      settled: false,
      fallbackTimer: null,
    };
    this.entries.set(element, entry);
    this.observer?.observe(element);

    if (!this.started && this.startTimer === null) {
      this.startTimer = setTimeout(() => {
        this.startTimer = null;
        this.started = true;
        this.pump();
      }, this.pacing.startDelayMs);
    } else if (this.started) {
      this.schedulePump();
    }

    return {
      loaded: () => this.settle(entry),
      release: () => this.release(entry),
    };
  }

  /** Nothing initializing, and nothing waiting that could still be admitted
   *  (the queue is empty or the cap is reached). */
  isSettled(): boolean {
    if (this.initializing > 0) return false;
    if (this.admittedCount >= this.pacing.maxAdmitted) return true;
    for (const entry of this.entries.values()) {
      if (!entry.admitted) return false;
    }
    return true;
  }

  private handleObserverEntries = (
    observed: IntersectionObserverEntry[],
  ): void => {
    for (const io of observed) {
      const entry = this.entries.get(io.target);
      if (!entry || entry.admitted) continue;
      entry.ratio = io.intersectionRatio;
    }
    // No pump here: admissions are slot/time driven, and the next pump reads
    // the freshest ratios when it fires.
  };

  /** Highest priority waiting entry: viewport first, then the cards with no
   *  poster to wait behind, then registration order. */
  private pickNext(): Entry | null {
    let best: Entry | null = null;
    for (const entry of this.entries.values()) {
      if (entry.admitted) continue;
      if (!best || this.ranksAbove(entry, best)) best = entry;
    }
    return best;
  }

  private ranksAbove(entry: Entry, best: Entry): boolean {
    if (entry.ratio !== best.ratio) return entry.ratio > best.ratio;
    if (entry.hasPoster !== best.hasPoster) return !entry.hasPoster;
    return entry.order < best.order;
  }

  private pump = (): void => {
    if (!this.started) return;

    // One task fills the pipe. Each admission costs the host a React commit
    // (~75 ms on a 300-node canvas), so admitting one per task paced the
    // canvas at the commit rate instead of at `maxConcurrentInits`.
    // `maxAdmitted` is a hard stop, not a budget: only a card leaving the
    // page gives its slot back.
    while (
      this.initializing < this.pacing.maxConcurrentInits &&
      this.admittedCount < this.pacing.maxAdmitted
    ) {
      const next = this.pickNext();
      if (!next) return;

      // ratio > 0 ⇔ within the observer's expanded root (or admitted blind
      // without IO — that path always trickles).
      const spacing =
        next.ratio > 0
          ? this.pacing.admitSpacingMs
          : this.pacing.offscreenTrickleSpacingMs;
      const sinceLast =
        this.lastAdmitAt === null
          ? Infinity
          : performance.now() - this.lastAdmitAt;
      if (sinceLast < spacing) {
        this.schedulePump(spacing - sinceLast);
        return;
      }

      next.admitted = true;
      this.initializing++;
      this.admittedCount++;
      this.lastAdmitAt = performance.now();
      this.observer?.unobserve(next.element);
      next.fallbackTimer = setTimeout(
        () => this.settle(next),
        INIT_FALLBACK_TIMEOUT_MS,
      );
      next.onAdmit();
    }
  };

  private schedulePump(delayMs = 0): void {
    // performance.now(): monotonic. Wall-clock here would let a backward
    // clock step (NTP, sleep-resume) arm a multi-minute timer that the
    // earlier-deadline check below could then never displace.
    const dueAt = performance.now() + delayMs;
    if (this.pumpTimer !== null) {
      // Keep the EARLIER of the two wake-ups: a freed slot's zero-delay pump
      // must not be swallowed by an already-armed spacing timer.
      if (dueAt >= this.pumpDueAt) return;
      clearTimeout(this.pumpTimer);
    }
    this.pumpDueAt = dueAt;
    this.pumpTimer = setTimeout(() => {
      this.pumpTimer = null;
      this.pumpDueAt = Infinity;
      this.pump();
    }, delayMs);
  }

  /** Return an admitted entry's slot (load event, timeout, or release). */
  private settle(entry: Entry): void {
    if (!entry.admitted || entry.settled) return;
    entry.settled = true;
    if (entry.fallbackTimer !== null) {
      clearTimeout(entry.fallbackTimer);
      entry.fallbackTimer = null;
    }
    this.initializing--;
    this.schedulePump();
  }

  private release(entry: Entry): void {
    // Identity, not key presence: a stale handle (this element re-registered
    // since) must not evict the live entry or double-return its slot.
    if (this.entries.get(entry.element) !== entry) return;
    this.entries.delete(entry.element);
    this.observer?.unobserve(entry.element);
    if (entry.admitted) {
      this.admittedCount--;
      // An already-settled card returned its init slot when it loaded; what
      // unmounting returns is its `maxAdmitted` slot, and nothing else would
      // wake the pump for it.
      if (entry.settled) this.schedulePump();
      else this.settle(entry);
    }
    if (this.entries.size === 0) this.resetWhenIdle();
  }

  /** Empty again (canvas unmounted): the next canvas gets a fresh settle
   *  window instead of inheriting a started scheduler mid-flight. */
  private resetWhenIdle(): void {
    this.started = false;
    if (this.startTimer !== null) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.pumpTimer !== null) {
      clearTimeout(this.pumpTimer);
      this.pumpTimer = null;
    }
    this.pumpDueAt = Infinity;
    this.initializing = 0;
    this.admittedCount = 0;
    this.lastAdmitAt = null;
  }
}

/** Shared instance — all cards on a page pace through one controller. */
export const iframeMountScheduler = new IframeMountScheduler();

/** The capture page's own controller: its own pacing, and its own cap. */
export const captureIframeMountScheduler = new IframeMountScheduler(
  undefined,
  CAPTURE_PACING,
);
